/**
 * LRC Parser for synchronized lyrics
 *
 * Standard LRC format:
 * [00:12.00]First line of lyrics
 * [00:17.25]Second line
 * [00:22.00][00:45.00]Repeated line (multiple timestamps)
 *
 * Enhanced LRC (A2) format with word-level timestamps:
 * [00:10.00]<00:10.00>This <00:10.25>is <00:10.50>the <00:10.75>first <00:11.00>line.
 */

export interface LrcWord {
  timestamp: number; // start time in seconds
  text: string;      // word text (may include trailing space)
}

export interface LrcLine {
  timestamp: number; // seconds
  text: string;
  words?: LrcWord[]; // only present for Enhanced LRC lines
}

export interface LrcMetadata {
  title?: string;
  artist?: string;
  album?: string;
  by?: string;
  offset?: number;
  length?: string;
}

export interface ParsedLrc {
  metadata: LrcMetadata;
  lines: LrcLine[];
  isEnhanced: boolean; // true if any line has word-level timestamps
}

// Regex patterns
const TIMESTAMP_REGEX = /\[(\d{2}):(\d{2})(?:[.:]\d{2,3})?\]/g;

/**
 * Parse a timestamp string to seconds
 * Supports formats: [mm:ss.xx], [mm:ss:xx], [mm:ss]
 */
function parseTimestamp(match: string): number {
  const parts = match.slice(1, -1).split(/[:.]/);
  const minutes = parseInt(parts[0], 10);
  const seconds = parseInt(parts[1], 10);
  const hundredths = parts[2] ? parseInt(parts[2].padEnd(2, '0').slice(0, 2), 10) : 0;

  return minutes * 60 + seconds + hundredths / 100;
}

/**
 * Parse LRC content string into structured data
 */
export function parseLrc(content: string): ParsedLrc {
  const lines = content.split('\n');
  const result: LrcLine[] = [];
  const metadata: LrcMetadata = {};
  let isEnhanced = false;

  for (const line of lines) {
    const trimmed = line.trim();
    if (!trimmed) continue;

    // Check for metadata tags like [ti:Title], [ar:Artist], etc.
    const metaMatch = trimmed.match(/^\[(\w+):(.+)\]$/);
    if (metaMatch && !trimmed.match(TIMESTAMP_REGEX)) {
      const [, key, value] = metaMatch;
      switch (key.toLowerCase()) {
        case 'ti':
          metadata.title = value;
          break;
        case 'ar':
          metadata.artist = value;
          break;
        case 'al':
          metadata.album = value;
          break;
        case 'by':
          metadata.by = value;
          break;
        case 'offset':
          metadata.offset = parseInt(value, 10);
          break;
        case 'length':
          metadata.length = value;
          break;
      }
      continue;
    }

    // Extract all timestamps from the line
    const timestamps: number[] = [];
    let match;
    const timestampRegex = /\[(\d{2}):(\d{2})(?:[.:]\d{2,3})?\]/g;

    while ((match = timestampRegex.exec(trimmed)) !== null) {
      timestamps.push(parseTimestamp(match[0]));
    }

    if (timestamps.length === 0) continue;

    // Extract the text after all line-level timestamps
    const rawText = trimmed.replace(/\[\d{2}:\d{2}(?:[.:]\d{2,3})?\]/g, '');

    // Check for Enhanced LRC (A2) inline word timestamps: <mm:ss.xx>
    const wordTimestampRegex = /<(\d{2}):(\d{2})(?:[.:]\d{2,3})?>/g;
    const hasWordTimestamps = wordTimestampRegex.test(rawText);
    wordTimestampRegex.lastIndex = 0; // reset after test

    let text: string;
    let words: LrcWord[] | undefined;

    if (hasWordTimestamps) {
      isEnhanced = true;
      words = [];
      // Split on word timestamps, capturing the timestamps
      const parts = rawText.split(/(<\d{2}:\d{2}(?:[.:]\d{2,3})?>)/);
      let currentWordTimestamp: number | null = null;

      for (const part of parts) {
        const tsMatch = part.match(/^<(\d{2}):(\d{2})(?:[.:]\d{2,3})?>$/);
        if (tsMatch) {
          // Parse inline timestamp (reuse parseTimestamp with bracket substitution)
          currentWordTimestamp = parseTimestamp('[' + part.slice(1, -1) + ']');
        } else if (currentWordTimestamp !== null && part.length > 0) {
          words.push({ timestamp: currentWordTimestamp, text: part });
          currentWordTimestamp = null;
        }
      }

      // Build plain text from words
      text = words.map(w => w.text).join('').trim();
    } else {
      text = rawText.trim();
    }

    // Create an entry for each timestamp (handles karaoke-style multiple timestamps)
    for (const timestamp of timestamps) {
      result.push({ timestamp, text, ...(words ? { words } : {}) });
    }
  }

  // Sort by timestamp
  result.sort((a, b) => a.timestamp - b.timestamp);

  // Apply offset if specified
  if (metadata.offset) {
    const offsetSeconds = metadata.offset / 1000;
    for (const line of result) {
      line.timestamp += offsetSeconds;
      if (line.words) {
        for (const word of line.words) {
          word.timestamp += offsetSeconds;
        }
      }
    }
  }

  return { metadata, lines: result, isEnhanced };
}

/**
 * Find the index of the current line based on playback time
 * Returns -1 if before the first line
 */
export function findCurrentLineIndex(lines: LrcLine[], currentTime: number): number {
  if (lines.length === 0) return -1;

  // Find the last line that has started
  for (let i = lines.length - 1; i >= 0; i--) {
    if (lines[i].timestamp <= currentTime) {
      return i;
    }
  }

  return -1;
}

/**
 * Find the index of the current word based on playback time
 * Returns -1 if before the first word
 */
export function findCurrentWordIndex(words: LrcWord[], currentTime: number): number {
  if (words.length === 0) return -1;

  for (let i = words.length - 1; i >= 0; i--) {
    if (words[i].timestamp <= currentTime) {
      return i;
    }
  }

  return -1;
}

/**
 * Format seconds to mm:ss display
 */
export function formatLrcTime(seconds: number): string {
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}
